import React, { useState, useEffect, useRef } from "react";
import styled from "styled-components";
import Draggable from "react-draggable";
import Modal from "react-modal";
import { HexColorPicker } from "react-colorful";
import "@fortawesome/fontawesome-free/css/all.min.css";

const TableNode = styled.div`
  width: ${(props) => `${props.width}px`};
  height: ${(props) => `${props.height}px`};
  border: 2px solid black;
  border-radius: 50px;
  background-color: ${(props) => props.color};
  text-align: center;
  position: absolute;
  cursor: move;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${(props) => `${props.fontSize}px`}; /* Dynamic font size */
  z-index: 0;
`;
const IDContainer = styled.div`
  width: ${(props) => `${props.fontSize * 2}px`}; /* Circular background */
  height: ${(props) => `${props.fontSize * 2}px`};
  border-radius: 50%;
  background-color: white; /* Contrast background */
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${(props) => `${props.fontSize}px`}; /* Dynamic font size */
  color: black; /* Static font color */
`;

const DeleteButton = styled.button`
  position: absolute;
  top: -7px;
  right: -7px;
  width: ${(props) => `${props.buttonSize}px`}; /* Dynamic button size */
  height: ${(props) => `${props.buttonSize}px`};
  padding: 0;
  font-size: ${(props) => `${props.buttonFontSize}px`}; /* Dynamic font size */
  line-height: 30px;
  background: grey;
  color: white;
  border: 1px solid white;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
`;
const StyledSelect = styled.select`
  width: 100%;
  padding: 10px 15px;
  border-radius: 10px;
  background-color: #f5f5f5;
  border: none;
  box-shadow: 0px 1px 3px rgba(0, 0, 0, 0.1);
  font-size: 16px;
  font-weight: bold;
  color: #2c3e50;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='14' height='10'%3E%3Cpath fill='%232c3e50' d='M7 10L0 0h14z'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: calc(100% - 10px) center;
  background-size: 10px;
  &:focus {
    outline: none;
    box-shadow: 0 0 3px rgba(0, 0, 0, 0.2);
  }

  @media (max-width: 768px) {
    padding: 8px 12px;
    font-size: 14px;
  }

  @media (max-width: 480px) {
    padding: 6px 10px;
    font-size: 12px;
  }
`;

const StyledInput = styled.input`
  width: 100%;
  padding: 10px 15px;
  border-radius: 10px;
  background-color: #f5f5f5;
  border: none;
  box-shadow: 0px 1px 3px rgba(0, 0, 0, 0.1);
  font-size: 16px;
  color: #2c3e50;
  margin-bottom: 15px;
  &:focus {
    outline: none;
    box-shadow: 0 0 3px rgba(0, 0, 0, 0.2);
  }

  @media (max-width: 768px) {
    padding: 8px 12px;
    font-size: 14px;
  }

  @media (max-width: 480px) {
    padding: 6px 10px;
    font-size: 12px;
  }
`;

const StyledH4 = styled.h4`
  font-size: 16px;
  font-weight: bold;
  color: #2c3e50;
  margin-bottom: 10px;

  @media (max-width: 768px) {
    font-size: 14px;
    margin-bottom: 8px;
  }

  @media (max-width: 480px) {
    font-size: 12px;
    margin-bottom: 6px;
  }
`;
Modal.setAppElement("#root");

function InteractiveTables({
  imageUrl,
  initialTables = [],
  onTableDataChange
}) {
  const [imageSize, setImageSize] = useState({ width: 1000, height: 500 });
  const [tables, setTables] = useState([]);
  const [selectedTable, setSelectedTable] = useState(null);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [modalIsOpen, setIsOpen] = useState(false);
  const previousTables = useRef(initialTables); // Store previous tables state
  const imageRef = useRef(null);

  // Update tables when initialTables changes (but only if they are different)
  useEffect(() => {
    if (
      JSON.stringify(initialTables) !== JSON.stringify(tables) &&
      initialTables &&
      Array.isArray(initialTables) &&
      initialTables.length > 0
    ) {
      const updatedTables = initialTables.map((table) => ({
        ...table,
        x: (table.xPercentage / 100) * imageSize.width,
        y: (table.yPercentage / 100) * imageSize.height,
        width: (table.widthPercentage / 100) * imageSize.width,
        height: (table.heightPercentage / 100) * imageSize.height,
        color: table?.color
      }));

      setTables(updatedTables);
      const newCategories = initialTables.reduce((acc, table) => {
        const categoryKey = `${table.name}_${table.people}_${table.color}`;
        if (!acc.some((cat) => cat.key === categoryKey)) {
          acc.push({
            key: categoryKey,
            name: table.name,
            price:
              Math.min(...table.pricesPerDay) ===
              Math.max(...table.pricesPerDay)
                ? `${Math.max(...table.pricesPerDay)}`
                : `${Math.min(...table.pricesPerDay)} - €${Math.max(...table.pricesPerDay)}`,
            pricesPerDay: table.pricesPerDay,
            daysOfWeek: table.daysOfWeek,
            people: table.people,
            color: table.color
          });
        }
        return acc;
      }, []);
      setExistingCategories(newCategories);
    }
  }, [initialTables]);
  useEffect(() => {
    if (
      imageLoaded &&
      initialTables.length > 0 &&
      imageSize.width &&
      imageSize.height
    ) {
      const imgElement = document.getElementById("table-image");
      const newWidth = imgElement.clientWidth;
      const newHeight = imgElement.clientHeight;
      setImageSize({ width: newWidth, height: newHeight });
      if (imgElement) {
        const updatedTables = initialTables.map((table) => ({
          ...table,
          x: (table.xPercentage / 100) * imageSize.width,
          y: (table.yPercentage / 100) * imageSize.height,
          width: (table.widthPercentage / 100) * imageSize.width,
          height: (table.heightPercentage / 100) * imageSize.height,
          color: table?.color
        }));
        setTables(updatedTables);
      }
    }
  }, [initialTables]);
  const handleImageLoad = (event) => {
    const { width, height } = event.target;
    setImageSize({ width, height });
    setImageLoaded(true); // Mark image as loaded
  };

  // Only call onTableDataChange when tables state actually changes
  useEffect(() => {
    if (JSON.stringify(tables) !== JSON.stringify(previousTables.current)) {
      onTableDataChange(tables);
      previousTables.current = tables; // Update the ref to current state
    }
  }, [tables, onTableDataChange]);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  const updateWindowDimensions = () => {
    setWindowWidth(window.innerWidth);
  };

  useEffect(() => {
    window.addEventListener("resize", updateWindowDimensions);

    return () => {
      window.removeEventListener("resize", updateWindowDimensions);
    };
  }, []);
  useEffect(() => {
    if (tables?.length === 0) setSelectedTable(null);
  }, [tables]);

  const customStyles = {
    content: {
      top: "50%",
      left: windowWidth > 900 ? "60%" : "50%",
      right: "auto",
      bottom: "auto",
      marginRight: "-50%",
      backgroundColor: "#F0F0F0",
      borderRadius: "2%",
      transform: "translate(-50%, -50%)",
      width: windowWidth < 700 ? "80%" : "60%",
      height: "60%"
    },
    overlay: {
      backgroundColor: "rgba(0, 0, 0, 0.5)"
    }
  };

  function openModal() {
    setIsOpen(true);
  }

  function closeModal() {
    setIsOpen(false);
  }

  const [tableForm, setTableForm] = useState({
    pricingType: "universal", // New field for pricing type
    price: "", // For universal pricing
    pricesPerDay: Array(7).fill(""),
    people: "",
    color: "#000000", // Default color for the color picker
    name: "",
    count: 1
  });
  const [existingCategories, setExistingCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("new");

  const isFormValid = () => {
    // Check if pricing is perDay, in which case all days must have valid prices
    if (tableForm.pricingType === "perDay") {
      // Ensure that no price is 0 or empty
      console.log("EROORRRRRR HERREEEEEEE");
      const allPricesValid = tableForm?.pricesPerDay.every(
        (price) => price !== "" && parseFloat(price) > 0
      );
      if (!allPricesValid) {
        return false; // If any price is invalid, the form is not valid
      }
    }

    // Validate the form for both universal and perDay pricing
    return (
      tableForm.price && // This will handle both universal and computed price ranges
      tableForm.people &&
      tableForm.color &&
      tableForm.name &&
      tableForm.count > 0
    );
  };

  useEffect(() => {
    const img = new Image();
    img.onload = () => {
      setImageSize({ width: img.width, height: img.height });
    };
    img.src = imageUrl;

    // Add event listener for window resize to handle responsive behavior
    const handleResize = () => {
      const imgElement = document.getElementById("table-image");
      if (imgElement) {
        const newWidth = imgElement.clientWidth;
        const newHeight = imgElement.clientHeight;
        setImageSize({ width: newWidth, height: newHeight });

        // Update table positions and sizes based on percentage values
        setTables((prevTables) =>
          prevTables.map((table) => ({
            ...table,
            x: (table.xPercentage / 100) * newWidth,
            y: (table.yPercentage / 100) * newHeight,
            width: (table.widthPercentage / 100) * newWidth, // Adjust size responsively
            height: (table.heightPercentage / 100) * newHeight // Adjust size responsively
          }))
        );
      }
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [imageUrl]);

  const onStop = (event, data, tableId) => {
    const imgElement = document.getElementById("table-image");
    const newWidth = imgElement.clientWidth;
    const newHeight = imgElement.clientHeight;

    const xPercentage = (data.x / newWidth) * 100;
    const yPercentage = (data.y / newHeight) * 100;

    setTables((prevTables) =>
      prevTables.map((table) =>
        table.id === tableId
          ? { ...table, x: data.x, y: data.y, xPercentage, yPercentage }
          : table
      )
    );
  };

  /*const addTable = () => {
    if (!isFormValid()) {
      alert("Please fill in all the required fields!");
      return;
    }

    const categoryKey = `${tableForm.name}_${tableForm.price}_${tableForm.people}_${tableForm.color}`;

    // First check if an existing category matches the new form data
    const existingCategory = existingCategories.find(
      (category) =>
        category.name === tableForm.name &&
        category.price === tableForm.price &&
        category.people === tableForm.people &&
        category.color === tableForm.color
    );

    if (selectedCategory === "new" && existingCategory) {
      alert("This category already exists. Please select it from the list.");
      return; // Prevent adding a duplicate category
    }
    if (selectedCategory === "new" && !existingCategory) {
      setExistingCategories((prev) => [
        ...prev,
        {
          key: categoryKey,
          name: tableForm.name,
          price: tableForm.price,
          people: tableForm.people,
          color: tableForm.color
        }
      ]);
    }

    if (!existingCategory) {
      setExistingCategories((prev) => [
        ...prev,
        {
          key: categoryKey,
          name: tableForm.name,
          price: tableForm.price,
          people: tableForm.people,
          color: tableForm.color
        }
      ]);
      setIsOpen(false);
    }

    setTables((prevTables) => {
      const imgElement = document.getElementById("table-image");
      const newWidth = imgElement.clientWidth;
      const newHeight = imgElement.clientHeight;

      const newTables = [];
      for (let i = 0; i < tableForm.count; i++) {
        const id = prevTables.length + newTables.length + 1;
        newTables.push({
          ...tableForm,
          id,
          x: 50,
          y: 50 + 1 * 120,
          xPercentage: (50 / newWidth) * 100,
          yPercentage: ((50 + 1 * 120) / newHeight) * 100,
          width: 50,
          height: 50,
          widthPercentage: (50 / newWidth) * 100, // Store the width as a percentage
          heightPercentage: (50 / newHeight) * 100 // Store the height as a percentage
        });
      }
      newTables.forEach((table) => {
        if (tableForm.pricingType === "universal") {
          table.price = tableForm.price;
          table.pricesPerDay = Array(7).fill(tableForm.price); // Map universal price to all days
        } else {
          table.pricesPerDay = tableForm.pricesPerDay;
        }
      });

      return [...prevTables, ...newTables];
    });
  };*/
  const addTable = () => {
    if (!isFormValid()) {
      alert("Please fill in all the required fields!");
      return;
    }

    const categoryKey = `${tableForm.name}_${tableForm.people}_${tableForm.color}`;

    // First check if an existing category matches the new form data
    const existingCategory = existingCategories.find(
      (category) =>
        category.name === tableForm.name &&
        category.price === tableForm.price &&
        category.people === tableForm.people &&
        category.color === tableForm.color
    );

    // If a category with the same key already exists and is selected as 'new'
    if (selectedCategory === "new" && existingCategory) {
      alert("This category already exists. Please select it from the list.");
      return; // Prevent adding a duplicate category
    }

    // If a new category is being created and doesn't exist yet, add it
    let maininfopricing;
    if (tableForm.pricingType === "universal") {
      maininfopricing = {
        price: tableForm.price,
        pricesPerDay: Array(7).fill(tableForm.price)
      };
      // Map universal price to all days
    } else {
      maininfopricing = tableForm.pricesPerDay;
    }
    if (selectedCategory === "new" && !existingCategory) {
      console.log();
      setExistingCategories((prev) => [
        ...prev,
        {
          key: categoryKey,
          name: tableForm.name,
          price: tableForm.price,
          people: tableForm.people,
          color: tableForm.color,
          ...maininfopricing
        }
      ]);
    }

    // Now create the new table
    setTables((prevTables) => {
      const imgElement = document.getElementById("table-image");
      const newWidth = imgElement.clientWidth;
      const newHeight = imgElement.clientHeight;

      const newTables = [];
      for (let i = 0; i < tableForm.count; i++) {
        const id = prevTables.length + newTables.length + 1;
        newTables.push({
          ...tableForm,
          id,
          x: 50,
          y: 50 + 1 * 120,
          xPercentage: (50 / newWidth) * 100,
          yPercentage: ((50 + 1 * 120) / newHeight) * 100,
          width: 50,
          height: 50,
          widthPercentage: (50 / newWidth) * 100, // Store the width as a percentage
          heightPercentage: (50 / newHeight) * 100 // Store the height as a percentage
        });
      }

      newTables.forEach((table) => {
        if (tableForm.pricingType === "universal") {
          table.price = tableForm.price;
          table.pricesPerDay = Array(7).fill(tableForm.price); // Map universal price to all days
        } else {
          table.pricesPerDay = tableForm.pricesPerDay;
        }
      });

      return [...prevTables, ...newTables];
    });

    // Close the modal after adding the table
    setIsOpen(false);
  };
  useEffect(() => {
    if (selectedCategory !== "new") {
      const existingCategory = existingCategories.find(
        (category) => category.key === selectedCategory
      );

      if (existingCategory) {
        console.log("existingCategory: ", existingCategory);
        // Check if all prices in pricesPerDay are the same
        const allPricesSame = existingCategory?.pricesPerDay?.every(
          (price) => price === existingCategory?.pricesPerDay[0]
        );
        console.log("WESHHHHH", allPricesSame, existingCategory);

        // Set pricingType based on whether prices are the same for all days or not
        const pricingType = allPricesSame ? "universal" : "perDay";

        // If prices are the same, use the first price for 'universal' pricing
        const price = allPricesSame
          ? existingCategory.pricesPerDay[0]
          : `${Math.min(...existingCategory.pricesPerDay)} - ${Math.max(
              ...existingCategory.pricesPerDay
            )}`;

        console.log("Inside The useEffect: ", price);

        // Update the form with the selected category's details
        setTableForm((prevForm) => ({
          ...prevForm,
          name: existingCategory.name,
          price, // Set the price only if it's universal
          people: existingCategory.people,
          color: existingCategory.color,
          count: 1, // Keep count editable by the user
          pricingType: pricingType,
          pricesPerDay: existingCategory.pricesPerDay // Fill prices per day if needed
        }));
      }
    } else {
      // Reset the form when "Create New Category" is selected
      setTableForm({
        pricingType: "universal",
        price: "",
        pricesPerDay: Array(7).fill(""),
        people: "",
        color: "#000000",
        name: "",
        count: 1
      });
    }
  }, [selectedCategory, existingCategories]);

  const deleteTable = (id) => {
    setTables((prevTables) => {
      const tableToDelete = prevTables.find((table) => table.id === id);
      const updatedTables = prevTables.filter((table) => table.id !== id);

      const remainingTablesForCategory = updatedTables.filter(
        (table) => table.name === tableToDelete.name
      );

      // If no more tables for this category, remove the category
      if (remainingTablesForCategory.length === 0) {
        setExistingCategories((prevCategories) =>
          prevCategories.filter(
            (category) => category.name !== tableToDelete.name
          )
        );
      }

      const renumberedTables = updatedTables.map((table, index) => ({
        ...table,
        id: index + 1
      }));

      if (
        renumberedTables.length === 0 ||
        (selectedTable && selectedTable.id === id)
      ) {
        setSelectedTable(null);
      }

      return renumberedTables;
    });
  };
  const resizeCategoryTables = (category, changeWidth, changeHeight) => {
    const imgElement = document.getElementById("table-image");
    const newWidth = imgElement.clientWidth;
    const newHeight = imgElement.clientHeight;

    // Update all tables in the same category
    setTables((prevTables) =>
      prevTables.map((table) => {
        if (table.name === category) {
          const newTableWidth = changeWidth;
          const newTableHeight = changeHeight;
          return {
            ...table,
            width: newTableWidth > 0 ? newTableWidth : table.width,
            height: newTableHeight > 0 ? newTableHeight : table.height,
            manualResize: true,
            widthPercentage: (newTableWidth / newWidth) * 100,
            heightPercentage: (newTableHeight / newHeight) * 100
          };
        }
        return table;
      })
    );

    // Update the selected table if it's in the same category
    setSelectedTable((prev) => {
      if (prev && prev.name === category) {
        const newTableWidth = changeWidth;
        const newTableHeight = changeHeight;
        return {
          ...prev,
          width: newTableWidth > 0 ? newTableWidth : prev.width,
          height: newTableHeight > 0 ? newTableHeight : prev.height,
          manualResize: true,
          widthPercentage: (newTableWidth / newWidth) * 100,
          heightPercentage: (newTableHeight / newHeight) * 100
        };
      }
      return prev;
    });
  };

  // Fixed the resizeTable function to trigger proper state update
  const resizeTable = (id, width, height, allnodes) => {
    const imgElement = document.getElementById("table-image");
    const newWidth = imgElement.clientWidth;
    const newHeight = imgElement.clientHeight;
    if (allnodes) {
      console.log("id , width, height : ", id, width, height);
      // Update the selected table's size and percentages if applicable
      const selectedTable = tables.find((table) => table.id === id);
      if (selectedTable) {
        // Resize all tables of the same category
        resizeCategoryTables(selectedTable.name, width, height);
      }
    } else {
      setTables((prevTables) =>
        prevTables.map((table) =>
          table.id === id
            ? {
                ...table,
                width: width > 0 ? width : table.width,
                height: height > 0 ? height : table.height,
                manualResize: true, // Mark as manually resized
                widthPercentage: (width / newWidth) * 100, // Update widthPercentage
                heightPercentage: (height / newHeight) * 100 // Update heightPercentage
              }
            : table
        )
      );
      if (selectedTable) {
        setSelectedTable((prev) => ({
          ...prev,
          width: width > 0 ? width : prev.width,
          height: height > 0 ? height : prev.height,
          manualResize: true,
          widthPercentage: (width / newWidth) * 100,
          heightPercentage: (height / newHeight) * 100
        }));
      }
    }
  };

  return (
    <div>
      <Modal
        isOpen={modalIsOpen}
        onRequestClose={closeModal}
        style={customStyles}
        contentLabel="Example Modal"
        ariaHideApp={false}
      >
        <form
          style={{
            position: "relative",
            top: 0,
            left: 0,
            zIndex: 10,
            backgroundColor: "transparent"
          }}
        >
          <h3>Add your table information</h3>
          <br />
          <StyledH4>Select the table category</StyledH4>
          <StyledSelect
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
          >
            <option value="new">Create New Category</option>
            {existingCategories.map((category, index) => (
              <option key={index} value={category.key}>
                {category.name} (Price: {category.price}, People:{" "}
                {category.people}, Color: {category.color})
              </option>
            ))}
          </StyledSelect>

          {/*<StyledH4>Price</StyledH4>
          <StyledInput
            type="text"
            name="price"
            placeholder="Price"
            value={tableForm.price}
            onChange={(e) =>
              setTableForm({ ...tableForm, price: e.target.value })
            }
            disabled={selectedCategory !== "new"}
          />*/}
          <StyledH4>Pricing Type</StyledH4>
          <StyledSelect
            value={tableForm.pricingType}
            onChange={(e) =>
              setTableForm({ ...tableForm, pricingType: e.target.value })
            }
            disabled={selectedCategory !== "new"}
          >
            <option value="universal">Same Pricing Every Day</option>
            <option value="perDay">Price Per Day</option>
          </StyledSelect>

          {tableForm.pricingType === "universal" && (
            <>
              <StyledH4>Price</StyledH4>
              <StyledInput
                type="text"
                name="price"
                placeholder="Price"
                value={tableForm.price}
                onChange={(e) =>
                  setTableForm({ ...tableForm, price: e.target.value })
                }
                disabled={selectedCategory !== "new"}
              />
            </>
          )}

          {tableForm.pricingType === "perDay" && (
            <>
              {/* Input fields for each day of the week */}
              <StyledH4>Prices Per Day</StyledH4>
              {[
                "Monday",
                "Tuesday",
                "Wednesday",
                "Thursday",
                "Friday",
                "Saturday",
                "Sunday"
              ].map((day, index) => (
                <div key={index}>
                  <label htmlFor={`price-${day}`}>{day}:</label>
                  <StyledInput
                    type="text"
                    id={`price-${day}`}
                    placeholder={`Price for ${day}`}
                    value={tableForm.pricesPerDay[index]}
                    onChange={(e) => {
                      const updatedPrices = [...tableForm.pricesPerDay];
                      updatedPrices[index] = e.target.value;

                      // Calculate the minimum and maximum price from updated prices
                      const minPrice = Math.min(...updatedPrices);
                      const maxPrice = Math.max(...updatedPrices);

                      // Set the price as a range or a single price if all prices are the same
                      const computedPrice =
                        minPrice === maxPrice
                          ? `${minPrice}`
                          : `${minPrice} - ${maxPrice}`;

                      setTableForm({
                        ...tableForm,
                        pricesPerDay: updatedPrices,
                        price: computedPrice // Dynamically update the price field with range or single price
                      });
                    }}
                    disabled={selectedCategory !== "new"}
                  />
                </div>
              ))}
            </>
          )}

          <StyledH4>Number of people in the table</StyledH4>
          <StyledInput
            type="number"
            name="people"
            placeholder="Number of People"
            value={tableForm.people}
            onChange={(e) =>
              setTableForm({ ...tableForm, people: e.target.value })
            }
            disabled={selectedCategory !== "new"}
          />

          <StyledH4>Choose the color</StyledH4>
          <div
            style={{
              display: "flex",
              flexDirection: "column", // Stack elements vertically
              alignItems: "center", // Center elements horizontally
              justifyContent: "center" // Center vertically
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexWrap: "wrap" // Ensures responsiveness for smaller screens
              }}
            >
              <HexColorPicker
                color={tableForm.color}
                onChange={(value) =>
                  setTableForm({ ...tableForm, color: value })
                }
              />
            </div>

            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                marginTop: "10px" // Add some space between picker and color preview
              }}
            >
              <div
                style={{
                  width: "40px",
                  height: "40px",
                  borderRadius: "50%",
                  backgroundColor: tableForm.color,
                  border: "1px solid #000",
                  marginRight: "10px"
                }}
              ></div>
              <div
                style={{
                  fontWeight: "bold",
                  fontSize: "16px",
                  width: "100px",
                  textAlign: "left",
                  overflow: "hidden",
                  whiteSpace: "nowrap",
                  textOverflow: "ellipsis"
                }}
              >
                {tableForm.color}
              </div>
            </div>
          </div>

          <StyledH4>Table category name</StyledH4>
          <StyledInput
            type="text"
            name="name"
            placeholder="Table Name"
            value={tableForm.name}
            onChange={(e) =>
              setTableForm({ ...tableForm, name: e.target.value })
            }
            disabled={selectedCategory !== "new"}
          />

          <StyledH4>Number of tables to Generate</StyledH4>
          <StyledInput
            type="number"
            name="count"
            placeholder="Number of Tables"
            value={tableForm.count}
            onChange={(e) =>
              setTableForm({ ...tableForm, count: e.target.value })
            }
          />
          <button
            type="button"
            style={{
              marginTop: "20px",
              padding: "10px 10px",
              margin: "0px 0",
              borderRadius: "20px",
              fontSize: "10px",
              fontWeight: "bold",
              background:
                "linear-gradient(to top right, rgba(91, 91, 91, 1), rgba(28, 57, 150, 1))",
              color: "white",
              border: "none",
              cursor: "pointer",
              boxShadow: "0 3px 3px rgba(0, 0, 0, 0.25)"
            }}
            onClick={addTable}
            disabled={!isFormValid()}
          >
            Add Table
          </button>
        </form>
      </Modal>

      {selectedTable && (
        <div
          style={{
            display: "flex",
            flexDirection: "row", // Default: row layout
            flexWrap: "wrap", // Allow wrapping to column if the screen is too small
            gap: "20px", // Add space between the divs
            justifyContent: "flex-start" // Align items at the start
          }}
        >
          <div
            className="TablesInfo"
            style={{
              backgroundColor: "#F0F0F0",
              borderRadius: "10px",
              padding: "20px", // Increased padding for a better look
              display: "inline-block", // Ensure background fits content
              boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.1)" // Add slight shadow for better style
            }}
          >
            <h3>Tables {selectedTable.name}</h3>
            <p>
              Price: €
              {Math.min(...selectedTable.pricesPerDay) ===
              Math.max(...selectedTable.pricesPerDay)
                ? Math.min(...selectedTable.pricesPerDay)
                : `${Math.min(...selectedTable.pricesPerDay)} - €${Math.max(...selectedTable.pricesPerDay)}`}
            </p>
            <p>People: {selectedTable.people} people</p>
            <p>Name: {selectedTable.name}</p>
            <button
              style={{
                display: "inline-flex", // Changed to inline-flex
                alignItems: "center",
                justifyContent: "center",
                marginTop: "20px",
                padding: "10px 10px",
                borderRadius: "20px",
                fontSize: "10px",
                fontWeight: "bold",
                background:
                  "linear-gradient(to top right, rgba(91, 91, 91, 1), rgba(28, 57, 150, 1))",
                color: "white",
                border: "none",
                cursor: "pointer",
                boxShadow: "0 3px 3px rgba(0, 0, 0, 0.25)",
                marginRight: "10px" // Add some spacing between buttons
              }}
              onClick={() =>
                resizeTable(
                  selectedTable.id,
                  selectedTable.width + 5,
                  selectedTable.height + 5,
                  true
                )
              }
            >
              <i className="fas fa-expand" style={{ marginRight: "5px" }}></i>{" "}
              {/* Enlarge icon */}
              Enlarge group
            </button>
            <button
              style={{
                display: "inline-flex", // Changed to inline-flex
                alignItems: "center",
                justifyContent: "center",
                marginTop: "20px",
                padding: "10px 10px",
                borderRadius: "20px",
                fontSize: "10px",
                fontWeight: "bold",
                background:
                  "linear-gradient(to top right, rgba(91, 91, 91, 1), rgba(28, 57, 150, 1))",
                color: "white",
                border: "none",
                cursor: "pointer",
                boxShadow: "0 3px 3px rgba(0, 0, 0, 0.25)",
                marginLeft: "15px"
              }}
              onClick={() =>
                resizeTable(
                  selectedTable.id,
                  selectedTable.width - 5,
                  selectedTable.height - 5,
                  true
                )
              }
            >
              <i className="fas fa-compress" style={{ marginRight: "5px" }}></i>{" "}
              {/* Reduce icon */}
              Reduce group
            </button>
          </div>
          <div
            className="TableInfo"
            style={{
              backgroundColor: "#F0F0F0",
              borderRadius: "10px",
              padding: "20px",
              display: "inline-block", // Ensure background fits content
              boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.1)" // Add shadow for style consistency
            }}
          >
            <h3>Selected Table Info {selectedTable.id}</h3>
            <p>ID: {selectedTable.id}</p>
            <p>
              Price: €
              {Math.min(...selectedTable.pricesPerDay) ===
              Math.max(...selectedTable.pricesPerDay)
                ? Math.min(...selectedTable.pricesPerDay)
                : `${Math.min(...selectedTable.pricesPerDay)} - €${Math.max(...selectedTable.pricesPerDay)}`}
            </p>
            <p>People: {selectedTable.people} people</p>
            <p>Name: {selectedTable.name}</p>
            <button
              style={{
                display: "inline-flex", // Changed to inline-flex
                alignItems: "center",
                justifyContent: "center",
                marginTop: "20px",
                padding: "10px 10px",
                borderRadius: "20px",
                fontSize: "10px",
                fontWeight: "bold",
                background:
                  "linear-gradient(to top right, rgba(91, 91, 91, 1), rgba(28, 57, 150, 1))",
                color: "white",
                border: "none",
                cursor: "pointer",
                boxShadow: "0 3px 3px rgba(0, 0, 0, 0.25)",
                marginRight: "10px" // Add some spacing between buttons
              }}
              onClick={() =>
                resizeTable(
                  selectedTable.id,
                  selectedTable.width + 5,
                  selectedTable.height + 5
                )
              }
            >
              <i className="fas fa-expand" style={{ marginRight: "5px" }}></i>{" "}
              {/* Enlarge icon */}
              Enlarge
            </button>
            <button
              style={{
                display: "inline-flex", // Changed to inline-flex
                alignItems: "center",
                justifyContent: "center",
                marginTop: "20px",
                padding: "10px 10px",
                borderRadius: "20px",
                fontSize: "10px",
                fontWeight: "bold",
                background:
                  "linear-gradient(to top right, rgba(91, 91, 91, 1), rgba(28, 57, 150, 1))",
                color: "white",
                border: "none",
                cursor: "pointer",
                boxShadow: "0 3px 3px rgba(0, 0, 0, 0.25)",
                marginLeft: "15px"
              }}
              onClick={() =>
                resizeTable(
                  selectedTable.id,
                  selectedTable.width - 5,
                  selectedTable.height - 5
                )
              }
            >
              <i className="fas fa-compress" style={{ marginRight: "5px" }}></i>{" "}
              {/* Reduce icon */}
              Reduce
            </button>
          </div>
        </div>
      )}

      <button
        style={{
          marginTop: "20px",
          padding: "10px 10px",
          margin: "0px 0",
          borderRadius: "20px",
          fontSize: "10px",
          fontWeight: "bold",
          background:
            "linear-gradient(to top right, rgba(91, 91, 91, 1), rgba(28, 57, 150, 1))",
          color: "white",
          border: "none",
          cursor: "pointer",
          boxShadow: "0 3px 3px rgba(0, 0, 0, 0.25)",
          marginBottom: "10px"
        }}
        onClick={openModal}
      >
        Add Tables +
      </button>
      <div
        id="table-image"
        style={{
          width: "100%",
          paddingBottom: "100%",
          position: "relative",
          overflow: "hidden",
          backgroundImage: `url(${imageUrl})`,
          backgroundSize: "contain",
          backgroundRepeat: "no-repeat",
          backgroundPosition: "center",
          borderRadius: "20px",
          backgroundColor: "#F0F0F0",
          border: "1px solid #F0F0F0"
        }}
      >
        <img
          src={imageUrl}
          alt="table-layout"
          style={{ display: "none" }}
          onLoad={handleImageLoad}
        />

        {tables.map((table) => {
          const fontSize = table.width / 3; // Calculate font size dynamically
          const buttonSize = table.width / 2.5; // Calculate delete button size
          const buttonFontSize = table.width / 5; // Delete button font size

          return (
            <Draggable
              key={table.id}
              position={{ x: table.x, y: table.y }}
              bounds="parent"
              onStop={(e, data) => onStop(e, data, table.id)}
              cancel="input,textarea,select,button"
            >
              <TableNode
                color={table.color}
                width={table.width}
                height={table.height}
                fontSize={fontSize} // Pass dynamic font size
                onClick={() => setSelectedTable(table)}
              >
                <IDContainer fontSize={fontSize}>{table.id}</IDContainer>
                <DeleteButton
                  buttonSize={buttonSize} // Pass dynamic button size
                  buttonFontSize={buttonFontSize} // Pass dynamic font size for delete button
                  onClick={() => deleteTable(table.id)}
                >
                  &times;
                </DeleteButton>
              </TableNode>
            </Draggable>
          );
        })}
      </div>
    </div>
  );
}

export default InteractiveTables;
